// The ToDo's calendar button (mobile ToDoComponent handleCalendarChoice, ReportPriorities
// calendar): a click adds the task to the user's calendars, or takes it out again. An
// appointment goes in on its own (the button starts on, a click removes it); a bookmark without
// a day first asks for one in a modal, then is added. Part of the Kjuup.io calendar integration.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import MaterialIcon from "../../components/MaterialIcon";
import appConfig from "../../config/appConfig";
import { $crud } from "../../helper/CrudFactory";
import { callAPI } from "../../helper/apiUtils";
import { addTodoToCalendar, calendarOptionFor, removeTodoFromCalendar } from "../../helper/todoCalendar";
import { shouldShowCompanyTrialEndedNote } from "../../utils/kjuupIoBillingNav";
import { canShowEmailCalendarIntegration } from "../../utils/kjuupIoEntitlement";
import { resolveStripeFlags } from "../../utils/stripeFeature";
import { ProModal, ProSpinner, useTodoText } from "./ToDoProParts";

const DEFAULT_TIME = "14:00";

/** "YYYY-MM-DD" of today in local time (the date input's format). */
function todayValue() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * `onChange(todo, patch)` gets { calendarSynced } after a change, `onScheduled(todo)` runs once a
 * bookmark got its day from the modal (the caller reloads its lists).
 * Returns { optionFor(todo) → null | { added, needsDate, loading }, toggle(todo), modal }.
 */
export function useTodoCalendar({ onChange, onScheduled } = {}) {
  const { t } = useTodoText();
  const history = useHistory();
  const profile = useSelector((state) => state.profile);
  const subscription = useSelector((state) => state.subscription);
  const removedSeq = useSelector((state) => state.todoCalendarRemovedSeq);
  const removedTodoId = useSelector((state) => state.todoCalendarRemovedTodoId);
  const [busyId, setBusyId] = useState("");
  const busyRef = useRef(false);
  // The bookmark asked for a day, with the picked day and time.
  const [dateFor, setDateFor] = useState(null);
  const [day, setDay] = useState("");
  const [time, setTime] = useState(DEFAULT_TIME);
  const [saving, setSaving] = useState(false);
  const handlers = useRef({});
  handlers.current = { onChange, onScheduled };

  // Its event deleted in Google / Outlook / CalDAV (socket todoCalendarSync): the ToDo left the
  // user's calendars, so its button turns off at once, also over a local "added".
  const lastRemovedSeqRef = useRef(removedSeq);
  useEffect(() => {
    if (removedSeq === lastRemovedSeqRef.current) return;
    lastRemovedSeqRef.current = removedSeq;
    if (removedTodoId && handlers.current.onChange) {
      handlers.current.onChange({ todoId: removedTodoId }, { calendarSynced: false });
    }
  }, [removedSeq, removedTodoId]);

  // The same gate as the ToDo page's calendar sync (mobile emailFeatureEnabled).
  const enabled = useMemo(() => {
    if (!appConfig.calendarFeatureEnabled) return false;
    const sub = subscription || {};
    const flags = resolveStripeFlags(profile, sub);
    return canShowEmailCalendarIntegration({
      isKjuupPlusMember: flags.isKjuupPlusMember,
      hasActiveKjuupIoPlan: !flags.stripeUiEnabled || !!sub.active,
      showCompanyTrialEndedNote: shouldShowCompanyTrialEndedNote(sub),
      stripeFeatureEnabled: flags.stripeFeatureEnabled,
      companyStripeFeatureEnabled: flags.companyStripeFeatureEnabled,
    });
  }, [profile, subscription]);

  const optionFor = (todo) => {
    if (!enabled) return null;
    const option = calendarOptionFor(todo);
    return option ? { ...option, loading: !!busyId && busyId === String(todo.todoId) } : null;
  };

  const askToConnect = async () => {
    try {
      await $crud.confirm({
        title: t("todo_calendar_connect_title", "No calendar connected", "Kein Kalender verbunden"),
        textContent: t(
          "todo_calendar_connect_body_web",
          "Connect a calendar in Calendar Sync settings to add ToDos to it.",
          "Verbinde in den Kalender-Sync-Einstellungen einen Kalender, um Aufgaben hinzuzufügen."
        ),
        options: { ok: t("todo_calendar_connect", "Connect", "Verbinden"), cancel: t("cancel", "Cancel", "Abbrechen") },
      });
    } catch (_) {
      return;
    }
    history.push("/profile-setting", { openSection: "calendar-sync" });
  };

  const send = async (todo, adding) => {
    const todoId = todo && todo.todoId;
    if (!todoId || busyRef.current) return false;
    busyRef.current = true;
    setBusyId(String(todoId));
    try {
      const result = adding ? await addTodoToCalendar(todoId) : await removeTodoFromCalendar(todoId);
      if (result.status) {
        if (handlers.current.onChange) handlers.current.onChange(todo, { calendarSynced: adding });
        // Every connected calendar failed: the server tries again with its next rounds.
        const unreached = adding && result.data && result.data.failed > 0 && !result.data.added;
        $crud.notify({
          type: "success",
          message: !adding
            ? t("todo_calendar_removed", "Removed from your calendar", "Aus deinem Kalender entfernt")
            : unreached
              ? t(
                  "todo_calendar_added_later",
                  "Added. Your calendar could not be reached; Kjuup will try again later.",
                  "Hinzugefügt. Dein Kalender war nicht erreichbar, Kjuup versucht es später erneut."
                )
              : t("todo_calendar_added", "Added to your calendar", "Zu deinem Kalender hinzugefügt"),
        });
        return true;
      }
      if (result.code === "no_calendar") {
        askToConnect();
      } else {
        $crud.notify({
          type: "error",
          message:
            result.code === "no_date"
              ? t("todo_calendar_no_date", "Only a ToDo with a date can be added to a calendar.", "Nur eine Aufgabe mit Datum kann zum Kalender hinzugefügt werden.")
              : t("todo_calendar_failed", "Your calendar could not be updated. Please try again.", "Dein Kalender konnte nicht aktualisiert werden. Bitte versuche es erneut."),
        });
      }
      return false;
    } finally {
      busyRef.current = false;
      setBusyId("");
    }
  };

  /** The calendar button: add or remove; a bookmark without a day opens the date modal. */
  const toggle = (todo) => {
    const option = optionFor(todo);
    if (!option || option.loading) return;
    if (option.needsDate) {
      const deadline = String(todo.bookmarkDeadline || "").slice(0, 10);
      setDay(/^\d{4}-\d{2}-\d{2}$/.test(deadline) ? deadline : todayValue());
      setTime(DEFAULT_TIME);
      setDateFor(todo);
      return;
    }
    send(todo, !option.added);
  };

  const closeDate = () => {
    if (!saving) setDateFor(null);
  };

  // The day first (the bookmark becomes a dated task), then into the calendars.
  const saveDate = async () => {
    const todo = dateFor;
    if (!todo || !day || saving) return;
    setSaving(true);
    try {
      const response = await callAPI(`/todo/${todo.todoId}`, {}, "PUT", {
        date: new Date(`${day}T${time || DEFAULT_TIME}:00`).toISOString(),
      });
      const data = response && response.data;
      if (!data || data.status === false) throw new Error((data && data.message) || "");
    } catch (error) {
      setSaving(false);
      $crud.notify({
        type: "error",
        message: (error && error.message) || t("something_went_wrong", "Something went wrong", "Etwas ist schiefgelaufen"),
      });
      return;
    }
    setSaving(false);
    setDateFor(null);
    await send(todo, true);
    if (handlers.current.onScheduled) handlers.current.onScheduled(todo);
  };

  const modal = (
    <ProModal
      open={!!dateFor}
      title={t("todo_calendar_date_title", "No date yet", "Noch kein Datum")}
      onClose={closeDate}
      closeLabel={t("close", "Close", "Schließen")}
      className="tdp-cal-date"
      footer={
        <div className="tdp-btn-row">
          <button type="button" className="kj-btn kj-btn--secondary tdp-btn-flex" onClick={closeDate} disabled={saving}>
            {t("cancel", "Cancel", "Abbrechen")}
          </button>
          <button type="button" className="kj-btn kj-btn--primary tdp-btn-flex" onClick={saveDate} disabled={!day || saving}>
            {saving ? <ProSpinner small /> : <MaterialIcon icon="calendar_add_on" size={20} />}
            <span>{t("todo_calendar_date_save", "Set date & add", "Datum setzen & hinzufügen")}</span>
          </button>
        </div>
      }
    >
      <p className="tdp-cal-date-text">
        {t(
          "todo_calendar_date_body_web",
          "This bookmarked task has no date yet. Set one to add it to your calendar.",
          "Diese vorgemerkte Aufgabe hat noch kein Datum. Lege eins fest, um sie zu deinem Kalender hinzuzufügen."
        )}
      </p>
      <div className="tdp-card">
        <label className="tdp-info-row">
          <MaterialIcon icon="event" size={22} className="tdp-info-icon" />
          <span className="tdp-info-label">{t("todo_date", "Date", "Datum")}</span>
          <input type="date" className="tdp-inline-input" value={day} onChange={(e) => setDay(e.target.value)} />
        </label>
        <label className="tdp-info-row tdp-info-row--divider">
          <MaterialIcon icon="schedule" size={22} className="tdp-info-icon" />
          <span className="tdp-info-label">{t("todo_time", "Time", "Uhrzeit")}</span>
          <input type="time" className="tdp-inline-input" value={time} onChange={(e) => setTime(e.target.value)} />
        </label>
      </div>
    </ProModal>
  );

  return { enabled, optionFor, toggle, modal };
}
